import { Analytics } from '@cobliteam/analytics';
import {
  type CrmReportProblemTicket,
  type CrmReportProblemTicketsDto,
  CrmRequester,
  type CrmSchedulingTicketSummaryDto,
  type CrmTicketDto,
  type CrmTicketsDto,
  FuscaVehiclesRequester,
  type LastAssociationDeviceVehicle,
  type UpdateCrmSchedulingFormDto,
} from '@cobliteam/api-lib';
import GraphQLCrmRequester from '@cobliteam/api-lib/src/dashboard/crm/GraphQLCrmRequester';
import { type TicketDeviceRequest, type UpdateContactTrainingInfoRequestDto, UserTrainingMediaActionType, type UUID } from '@cobliteam/common-types';
import { permissions, usePermissions } from '@cobliteam/permissions';
import { Environment } from '@cobliteam/react-environments';
import { type UseQueryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import useTrainingVideo from '../useTrainingVideo';
import useUser from '../useUser';

export const CRM_TICKETS_SCHEDULE_INSTALLATION_QUERY_KEY = 'CRM_TICKETS_SCHEDULE_INSTALLATION';
export const CRM_SCHEDULING_SUMMARY_QUERY_KEY = 'CRM_SCHEDULING_SUMMARY_QUERY_KEY';

export const CRM_SHIPPING_QUERY_KEY = 'CRM_TICKETS_SHIPPING';
export const CRM_SCHEDULING_QUERY_KEY = 'CRM_TICKETS_SCHEDULING';
export const CRM_DEVOLUTION_QUERY_KEY = 'CRM_TICKETS_DEVOLUTION';

const env = Environment.getInstance().getEnvironment();

// Lazy instantiation to avoid circular dependency issues
const getCrmRequester = () => new CrmRequester({ host: env.apiHost });
const getFuscaVehiclesRequester = () =>
  new FuscaVehiclesRequester({
    host: env.apiHost,
  });

interface UpdateSchedulingTicketProps {
  ticketId: string;
  data: CrmTicketDto;
}

const useCrm = () => {
  const { getSession } = useUser();
  const fleetId = getSession().fleet.id;
  const userId = getSession().user.id;

  const queryClient = useQueryClient();
  const graphgQLRequester = new GraphQLCrmRequester();
  const { isAccessAllowed } = usePermissions();

  const { useStartUserTrainingMedia, useFinishUserTrainingMedia } = useTrainingVideo();
  const { mutate: updateStartUserTrainingMedia } = useStartUserTrainingMedia();
  const { mutate: updateFinishUserTrainingMedia } = useFinishUserTrainingMedia();

  const useSchedulingTickets = ({ options }: { options?: Omit<UseQueryOptions<CrmTicketsDto>, 'queryKey' | 'queryFn'> } = {}) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    const hasCrmPermission = isAccessAllowed(permissions.CREATE_UPDATE_DELETE_CRM);
    return useQuery<CrmTicketsDto>({
      queryKey: [CRM_SCHEDULING_QUERY_KEY, fleetId],
      queryFn: () => getCrmRequester().getSchedulingTickets(),
      ...options,
      enabled: hasCrmPermission && (options?.enabled ?? true),
    });
  };

  const updateSchedulingTicket = ({ ticketId, data }: UpdateSchedulingTicketProps) => {
    queryClient.setQueryData([CRM_SCHEDULING_QUERY_KEY, fleetId], (old: CrmTicketsDto | undefined) => {
      if (!old) return old;

      const tickets = old.tickets.map((item) => {
        if (item.id === ticketId) {
          return {
            ...item,
            ...data,
          };
        }
        return item;
      });
      return {
        tickets,
        total: old.total,
      };
    });
  };

  const useNationalHolidays = () => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<Date[]>({
      queryKey: ['NATIONAL-HOLIDAYS'],
      queryFn: async () => await graphgQLRequester.getSchedulingHolidays(),
    });
  };

  const useShippingTickets = () => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<CrmTicketsDto>({
      queryKey: [CRM_SHIPPING_QUERY_KEY, fleetId],
      queryFn: () => getCrmRequester().getShippingTickets(),
    });
  };

  const useSchedulingTicketsSummary = ({ options }: { options?: Omit<UseQueryOptions<CrmSchedulingTicketSummaryDto>, 'queryKey' | 'queryFn'> } = {}) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<CrmSchedulingTicketSummaryDto>({
      queryKey: [CRM_SCHEDULING_SUMMARY_QUERY_KEY, fleetId],
      queryFn: () => getCrmRequester().getSchedulingTicketsSummary(),
      ...options,
    });
  };

  const useDevolutionTickets = () => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<CrmTicketsDto>({
      queryKey: [],
      queryFn: () => getCrmRequester().getDevolutionTickets(),
    });
  };

  const useDevicesLastAssociations = (devicesIds: any, { enabled = true, refetchOnMount = false, ...options }: Partial<UseQueryOptions<LastAssociationDeviceVehicle[]>>) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<LastAssociationDeviceVehicle[]>({
      queryKey: [CRM_DEVOLUTION_QUERY_KEY, 'LAST_ASSOCIATIONS', devicesIds],
      queryFn: () => getCrmRequester().getLastAssociations(devicesIds),
      ...options,
      enabled,
      refetchOnMount,
    });
  };

  const useNotResponsibleForInstallation = (ticketId: string) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useMutation<string, undefined>({
      mutationFn: async () => getCrmRequester().notResponsible(ticketId),
      onMutate: async () => {
        const previousScheduleTickets: CrmTicketsDto | undefined = queryClient.getQueryData([CRM_SCHEDULING_QUERY_KEY, fleetId]);

        queryClient.setQueryData([CRM_SCHEDULING_QUERY_KEY, fleetId], (old: CrmTicketsDto | undefined) => {
          if (!old) return old;

          const tickets = old.tickets.map((ticket) => {
            if (ticket.id === ticketId) {
              return {
                ...ticket,
                installationInformation: {
                  ...ticket.installationInformation,
                  notResponsible: [userId],
                },
              };
            }
            return ticket;
          });

          return {
            ...old,
            tickets,
          };
        });

        return { previousScheduleTickets };
      },
    });
  };

  const useResponsibleForInstallation = (ticketId: string) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useMutation<string, undefined>({
      mutationFn: async () => getCrmRequester().responsible(ticketId),
      onMutate: async () => {
        const previousScheduleTickets: CrmTicketsDto | undefined = queryClient.getQueryData([CRM_SCHEDULING_QUERY_KEY, fleetId]);

        queryClient.setQueryData([CRM_SCHEDULING_QUERY_KEY, fleetId], (old: CrmTicketsDto | undefined) => {
          if (!old) return old;

          const tickets = old.tickets.map((ticket) => {
            if (ticket.id === ticketId) {
              return {
                ...ticket,
                installationInformation: {
                  ...ticket.installationInformation,
                  responsibleUser: {
                    ...ticket.installationInformation.responsibleUser,
                    id: userId,
                  },
                },
              };
            }
            return ticket;
          });

          return {
            ...old,
            tickets,
          };
        });

        return { previousScheduleTickets };
      },
    });
  };

  const useConfirmInstallation = (ticketId: string) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useMutation<string, undefined>({
      mutationFn: () => getCrmRequester().confirmInstallation(ticketId),
      onMutate: async () => {
        const previousScheduleTickets: CrmTicketsDto | undefined = queryClient.getQueryData([CRM_SCHEDULING_QUERY_KEY, fleetId]);

        queryClient.setQueryData([CRM_SCHEDULING_QUERY_KEY, fleetId], (old: CrmTicketsDto | undefined) => {
          if (!old) return old;

          const tickets = old.tickets.map((ticket) => {
            if (ticket.id === ticketId) {
              return {
                ...ticket,
                installationInformation: {
                  ...ticket.installationInformation,
                  installationConfirmedAt: new Date().toISOString(),
                },
              };
            }
            return ticket;
          });

          return {
            ...old,
            tickets,
          };
        });

        return { previousScheduleTickets };
      },
      onError: () => {
        toast.error('Erro ao confirmar a instalação.');
      },
      onSuccess: () => {
        toast.success('Instalação confirmada com sucesso!');
      },
    });
  };

  const useCancelInstallation = (ticketId: string) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useMutation<undefined, undefined>({
      mutationFn: async () => getCrmRequester().cancelInstallation(ticketId),
      onMutate: async () => {
        const previousScheduleTickets: CrmTicketsDto | undefined = queryClient.getQueryData([CRM_SCHEDULING_QUERY_KEY, fleetId]);

        queryClient.setQueryData([CRM_SCHEDULING_QUERY_KEY, fleetId], (old: CrmTicketsDto | undefined) => {
          if (!old) return old;

          const tickets = old.tickets.map((ticket) => {
            if (ticket.id === ticketId) {
              return {
                ...ticket,
                pipelineStage: '34178455',
              };
            }
            return ticket;
          });

          return {
            ...old,
            tickets,
          };
        });

        return { previousScheduleTickets };
      },
      onError: () => {
        toast.error('Erro ao cancelar a instalação.');
      },
    });
  };

  const useRateInstallation = (ticketId: string) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useMutation({
      mutationFn: () => getCrmRequester().rateInstallation(ticketId),
      onMutate: async () => {
        const previousScheduleTickets: CrmTicketsDto | undefined = queryClient.getQueryData([CRM_SCHEDULING_QUERY_KEY, fleetId]);

        queryClient.setQueryData([CRM_SCHEDULING_QUERY_KEY, fleetId], (old: CrmTicketsDto | undefined) => {
          if (!old) return old;

          const tickets = old.tickets.map((ticket) => {
            if (ticket.id === ticketId) {
              return {
                ...ticket,
                installationInformation: {
                  ...ticket.installationInformation,
                  ratedAt: new Date().toISOString(),
                },
              };
            }
            return ticket;
          });

          return {
            ...old,
            tickets,
          };
        });

        return { previousScheduleTickets };
      },
      onError: () => {
        toast.error('Erro ao avaliar a instalação.');
      },
      onSuccess: () => {
        toast.success('Avaliação enviada com sucesso!');
      },
    });
  };

  const useGetTicket = (deviceId: string, options?: Partial<UseQueryOptions<any>>) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<CrmReportProblemTicketsDto>({
      queryKey: [CRM_TICKETS_SCHEDULE_INSTALLATION_QUERY_KEY, deviceId, 'TICKETS'],
      queryFn: () => getCrmRequester().searchTicket(deviceId),
      ...options,
    });
  };

  const useCreateTicket = (deviceId: string) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useMutation({
      mutationFn: (ticketRequest: TicketDeviceRequest) => {
        return getCrmRequester().reportProblem(deviceId, ticketRequest);
      },
      onMutate: async (newTicket) => {
        const KEY_QUERY = [CRM_TICKETS_SCHEDULE_INSTALLATION_QUERY_KEY, deviceId, 'TICKETS'];

        await queryClient.cancelQueries({ queryKey: KEY_QUERY });
        const previousTickets: CrmReportProblemTicketsDto | undefined = queryClient.getQueryData(KEY_QUERY);

        queryClient.setQueryData<CrmReportProblemTicketsDto | undefined>(KEY_QUERY, (data: any) => {
          if (!data) return data;

          const tempTicket: CrmReportProblemTicket = {
            id: 'Aguardando Nº do ticket...',
            createdAt: new Date().toISOString(),
            properties: {
              subject: newTicket.problem,
            },
          };

          return {
            ticket: {
              total: data.tickets?.length ? data.total + 1 : 1,
              results: [tempTicket, ...(data?.tickets ?? [])],
            },
          };
        });

        return { previousTickets };
      },
      onError: (_, __, context) => {
        queryClient.setQueryData([CRM_TICKETS_SCHEDULE_INSTALLATION_QUERY_KEY, deviceId, 'TICKETS'], context?.previousTickets);
      },
    });
  };

  const useGetDealIdHubspot = () => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery({
      queryKey: [CRM_TICKETS_SCHEDULE_INSTALLATION_QUERY_KEY, 'DEAL_ID_HUBSPOT', fleetId],
      queryFn: () => getCrmRequester().getHubpotDealId(fleetId),
      enabled: true,
      staleTime: Number.POSITIVE_INFINITY,
    });
  };

  const useUpdateSchedulingTicketForm = () => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useMutation<CrmTicketDto, undefined, UpdateCrmSchedulingFormDto>({
      mutationFn: async (formRequest: UpdateCrmSchedulingFormDto) => {
        return getCrmRequester().updateSchedulingTicketForm(formRequest);
      },
      onMutate: async () => {
        const ticket: CrmTicketDto | undefined = queryClient.getQueryData([CRM_SCHEDULING_QUERY_KEY, fleetId]);

        return { ticket };
      },
      onSuccess: () => {
        toast.success('Formulário enviado com sucesso!', {
          closeButton: true,
        });
        queryClient.invalidateQueries({ queryKey: ['CRM_ORDERS'] });
      },
      onError: () => {
        toast.error('Erro ao agendar a instalação.');
      },
    });
  };

  const useDisassociateDeviceFromVehicle = useMutation<void, unknown, UUID>({
    mutationFn: (vehicleId: UUID) => getFuscaVehiclesRequester().removeDevice(vehicleId),
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: ['DEVICES'],
      });
    },
  });

  const useUpdateContactTrainings = (trainingMediaId: UUID, action: UserTrainingMediaActionType) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useMutation<any, unknown, UpdateContactTrainingInfoRequestDto>({
      mutationFn: (userTrainings: UpdateContactTrainingInfoRequestDto) => graphgQLRequester.updateContactTrainings(userTrainings),
      onSuccess: async (data: any) => {
        if (data.errors) return;

        if (action == UserTrainingMediaActionType.START) {
          await updateStartUserTrainingMedia(trainingMediaId);
          Analytics.track('Started training');
        } else if (action == UserTrainingMediaActionType.FINISH) {
          Analytics.track('Finished training');
          await updateFinishUserTrainingMedia(trainingMediaId);
        }
      },
    });
  };

  return {
    useSchedulingTickets,
    updateSchedulingTicket,
    useShippingTickets,
    useSchedulingTicketsSummary,
    useNationalHolidays,
    useNotResponsibleForInstallation,
    useResponsibleForInstallation,
    useConfirmInstallation,
    useCancelInstallation,
    useRateInstallation,
    useGetTicket,
    useCreateTicket,
    useGetDealIdHubspot,
    useDevolutionTickets,
    useDevicesLastAssociations,
    useDisassociateDeviceFromVehicle,
    useUpdateSchedulingTicketForm,
    useUpdateContactTrainings,
  };
};

export default useCrm;
